import * as Colyseus from '@colyseus/sdk';
import { getStateCallbacks } from '@colyseus/sdk';
import { authService } from '../services/SupabaseClient';
import { DISCONNECT_CODES, WORLD_CONFIG } from '@astra/shared';
import { emitGameEvent } from '../core/GameEvents';

export interface RemotePlayerData {
  id: string;
  sessionId: string;
  username: string;
  corpTag?: string;
  avatarUrl: string;
  shipClassId: string;
  shipColor: number;
  x: number;
  y: number;
  rotation: number;
  velocityX: number;
  velocityY: number;
  thrusting: boolean;
  hull: number;
  maxHull: number;
  shield: number;
  maxShield: number;
  totalPower: number;
  slots?: import('@astra/shared').SlotDefinition[];
  // Docking state
  isDocked?: boolean;
  isDockingAnimation?: boolean;
  dockedStationId?: string;
  dockPosition?: number;
  // Planet management state
  isManagingPlanet?: boolean;
  // Warp travel state
  warpPhase?: string;
  warpTargetX?: number;
  warpTargetY?: number;
  warpSpeed?: number;
  // Combat-overhaul: natural shield state
  shieldActive?: boolean;
  shieldRadius?: number;
  shieldRaisedAt?: number;
  shieldExpansionMs?: number;
  isOverheated?: boolean;
  isVenting?: boolean;
  // Stun state (collision recovery)
  isStunned?: boolean;
  stunEndTime?: number;
  // Boost state
  isBoosting?: boolean;
  // Beam weapon state
  beamFiring?: boolean;
  // DEV: dummy enemy flag
  isDummy?: boolean;
}

export interface AsteroidData {
  id: string;
  x: number;
  y: number;
  size: string;
  resourceType: string;
  resourceAmount: number;
  health: number;
  maxHealth: number;
}

// V1 harvestable fields — same shape as AsteroidData with a resourceTier hint
// and optional parent id (zoneId for ice/wreckage, nebulaId for gas pockets).
export interface HarvestableEntityData {
  id: string;
  x: number;
  y: number;
  size: string;
  resourceType: string;
  resourceTier: number;
  resourceAmount: number;
  health: number;
  maxHealth: number;
  zoneId?: string;
  nebulaId?: string;
}

// Solar system data (for constellation map display - shows as single icon)
export interface SystemData {
  id: string;
  name: string;
  x: number;
  y: number;
  starType: string;
  sunRadius: number;
}

export interface PlanetData {
  id: string;
  name: string;
  systemId: string;
  x: number;
  y: number;
  radius: number;
  planetType: string;
  orbitalDistance: number;
  orbitalSpeed: number;
  orbitalAngle: number;
  profileSeed: number;
  clusterDensity: number;
  /** Profile size multiplier (0.5..2.0) — picks the hex mesh frequency. */
  sizeMultiplier: number;
}

export interface StationData {
  id: string;
  name: string;
  x: number;
  y: number;
}

export interface PrivateStationData {
  id: string;
  ownerPlayerId: string;
  ownerName: string;
  name: string;
  x: number;
  y: number;
  tier: number;
  status: string;
  droneCapacity: number;
}

export interface SettlementClientData {
  id: string;
  name: string;
  x: number;
  y: number;
  tier: number;
  coreHull: number;
  coreMaxHull: number;
  coreExposed: boolean;
}

export interface DropItemEntry {
  itemType: 'credits' | 'resource' | 'equipment';
  itemId: string;       // '' for credits
  quantity: number;     // for credits, the credit amount
}

export interface ShipCargoSnapshot {
  capacity: number;
  used: number;
  items: Array<{ itemType: 'resource' | 'equipment'; itemId: string; quantity: number; volume: number }>;
}

export interface DropData {
  id: string;
  x: number;
  y: number;
  items: DropItemEntry[];
  ownerSessionId: string;
  ownerUntil: number;
  createdAt: number;
  expiresAt: number;
}

export interface DroneData {
  id: string;
  droneType: 'miner' | 'hauler' | 'fighter';
  ownerType: string;
  ownerId: string;
  homeStationId: string;
  x: number;
  y: number;
  velocityX: number;
  velocityY: number;
  rotation: number;
  hull: number;
  maxHull: number;
  shield: number;
  maxShield: number;
  state: string;
  targetId?: string;
  warpPhase?: string;
  isRogue: boolean;
  shipDefId?: string;
  /** Drone level (1..4) — synced from server DroneState.level. */
  level?: number;
}

export interface SunData {
  id: string;
  name: string;
  x: number;
  y: number;
  radius: number;
  starType: string;
  damageRadius: number;
  baseDamagePerSecond: number;
  sunSeed: number;
}

export interface NebulaData {
  id: string;
  name: string;
  x: number;
  y: number;
  radius: number;
  nebulaType: string;
  resourceType: string;
  density: number;
  colorR: number;
  colorG: number;
  colorB: number;
}

export interface GatewayData {
  id: string;
  targetConstellationId: string;
  targetConstellationName: string;
  targetConstellationX: number;
  targetConstellationY: number;
  travelCost: number;
  x: number;
  y: number;
}

// NPC Shop types (client-side)
export interface ShopData {
  id: string;
  station_id: string;
  shop_type: string;
}

export interface ConstellationEconomyData {
  tier: number;
  zoneType: string;
  resourceBonus: string | null;
}

export interface ShopInventoryItem {
  itemType: string;
  itemId: string;
  stock: number;
  maxStock: number;
  restockRate: number;
  buyPrice: number;
  sellPrice: number;
  name: string;
  tier: number;
  category?: string;
}

export interface SellableItemData {
  itemType: string;
  itemId: string;
  quantity: number;
  buyPrice: number;
  totalBuyPrice: number;
  name: string;
  canSell?: boolean;
  warning?: string;
}

// Inventory management types
export interface InventorySlotData {
  slotIndex: number;
  slotType: string;
  size: string;
  position: string;
  equipped: string;
}

export interface InventoryItemData {
  itemType: 'resource' | 'equipment';
  itemId: string;
  quantity: number;
}

export interface ShipInventoryItem {
  itemType: 'equipment' | 'resource';
  itemId: string;
  quantity: number;
}

export interface InventoryData {
  slots: InventorySlotData[];
  shipInventory?: ShipInventoryItem[];  // Equipment and resources in ship cargo
  shipClassId: string;
  shipName: string;
  credits: number;
}

export interface EquipResult {
  success: boolean;
  error?: string;
}

export interface UnequipResult {
  success: boolean;
  error?: string;
}

export interface DiscardResult {
  success: boolean;
  error?: string;
}

// Bank storage types
export interface BankItemData {
  itemType: 'equipment' | 'resource';
  itemId: string;
  quantity: number;
}

export interface BankTransferResult {
  success: boolean;
  error?: string;
}

// Player ship data (for station shipyard)
export interface PlayerShipData {
  id: string;
  shipClassId: string;
  customName?: string;
  hull: number;
  dockedAt?: string;
  slots: Array<{ slotIndex: number; equipped: string }>;
}

// Chat message types
export type ChatChannel = 'global' | 'local' | 'party' | 'corp';

export interface ChatMessageData {
  id: string;
  playerId: string;
  playerName: string;
  channel: ChatChannel;
  message: string;
  timestamp: number;
  isSystem?: boolean;
}

// Universe map data types
export interface UniverseMapConstellationData {
  x: number;
  y: number;
  name: string;
  tier: number;
  zoneType: string;
}

export interface UniverseMapConnectionData {
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  costAtoB: number;
  costBtoA: number;
}

export interface UniverseMapData {
  constellations: UniverseMapConstellationData[];
  connections: UniverseMapConnectionData[];
}

// Per-constellation preview payload for the universe-map sidepanel.
// `error` is set when the lookup fails (constellation not found, db error, etc).
export interface ConstellationPreviewData {
  constellationX: number;
  constellationY: number;
  name?: string;
  tier?: number;
  zoneType?: string;
  resourceBonus?: string | null;
  maxPrivateStations?: number;
  stations?: Array<{ name: string; tier: number }>;
  privateStationCount?: number;
  systems?: Array<{ name: string; starType: string | null; planetCount: number }>;
  gateways?: Array<{ targetX: number; targetY: number; targetName: string; travelCost: number }>;
  onlinePlayers?: number;
  error?: string;
}

class NetworkManager {
  private client: Colyseus.Client;
  private room: Colyseus.Room | null = null;

  // Reconnection state
  private reconnectAttempts: number = 0;
  private readonly MAX_RECONNECT_ATTEMPTS = 5;
  private readonly RECONNECT_DELAY = 2000; // ms
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private lastConstellationX: number = 0;
  private lastConstellationY: number = 0;
  private isReconnecting: boolean = false;
  private isIntentionalDisconnect: boolean = false; // Flag to prevent auto-reconnect during gateway travel

  // RTT measurement
  private rtt: number = 0;                   // Latest round-trip time in ms
  private rttSmoothed: number = 0;           // Exponentially smoothed RTT

  // Cache the most recent tutorial_state from the server. Necessary because
  // the panel mounts after appState flips to 'playing', which is AFTER the
  // server's onJoin send — without the cache, the panel misses step 0.
  private latestTutorialState: { step: number; title: string; body: string; totalSteps: number; mission: 'mine' | 'kill' | null } | null = null;
  // Cache the most recent tutorial_mission_progress so the panel can read it on
  // mount and disable Continue without waiting for the next 1Hz broadcast.
  private latestTutorialMissionProgress: { step: number; mission: 'mine' | 'kill'; progress: number; threshold: number; complete: boolean } | null = null;
  // Beacon range cache. GameScene's edge-trigger emit can fire before the
  // panel mounts (player spawned inside the beacon → first frame transitions
  // false→true → mitt drops the event). Panel reads this on mount.
  private latestBeaconInRange: boolean = false;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private readonly PING_INTERVAL = 5000;     // Ping every 5 seconds

  // Event callbacks
  public onConnected: (() => void) | null = null;
  public onDisconnected: ((code: number) => void) | null = null;
  public onReconnecting: ((attempt: number, maxAttempts: number) => void) | null = null;
  public onReconnectFailed: (() => void) | null = null;
  public onError: ((error: Error) => void) | null = null;

  // Player events
  public onLocalPlayerLoaded: ((player: RemotePlayerData) => void) | null = null;
  public onPlayerJoin: ((player: RemotePlayerData) => void) | null = null;
  public onPlayerLeave: ((sessionId: string) => void) | null = null;
  public onPlayerUpdate: ((sessionId: string, data: Partial<RemotePlayerData>) => void) | null = null;

  // World events
  public onAsteroidAdd: ((asteroid: AsteroidData) => void) | null = null;
  public onAsteroidRemove: ((asteroidId: string) => void) | null = null;
  public onAsteroidUpdate: ((asteroidId: string, data: Partial<AsteroidData>) => void) | null = null;

  // V1 harvestable callbacks (ice / gas / wreckage)
  public onIceAsteroidAdd: ((entity: HarvestableEntityData) => void) | null = null;
  public onIceAsteroidRemove: ((id: string) => void) | null = null;
  public onIceAsteroidUpdate: ((id: string, data: Partial<HarvestableEntityData>) => void) | null = null;
  public onGasPocketAdd: ((entity: HarvestableEntityData) => void) | null = null;
  public onGasPocketRemove: ((id: string) => void) | null = null;
  public onGasPocketUpdate: ((id: string, data: Partial<HarvestableEntityData>) => void) | null = null;
  public onWreckageAdd: ((entity: HarvestableEntityData) => void) | null = null;
  public onWreckageRemove: ((id: string) => void) | null = null;
  public onWreckageUpdate: ((id: string, data: Partial<HarvestableEntityData>) => void) | null = null;
  public onSystemAdd: ((system: SystemData) => void) | null = null;
  public onSystemRemove: ((systemId: string) => void) | null = null;
  public onPlanetAdd: ((planet: PlanetData) => void) | null = null;
  public onPlanetChange: ((planetId: string, data: Partial<PlanetData>) => void) | null = null;
  public onPlanetRemove: ((planetId: string) => void) | null = null;
  public onStationAdd: ((station: StationData) => void) | null = null;
  public onPrivateStationAdd: ((station: PrivateStationData) => void) | null = null;
  public onPrivateStationUpdate: ((stationId: string, data: Partial<PrivateStationData>) => void) | null = null;
  public onPrivateStationRemove: ((stationId: string) => void) | null = null;

  // Settlements (emergent rogue-drone nests — docs/drone_settlement.md)
  public onSettlementAdd: ((s: SettlementClientData) => void) | null = null;
  public onSettlementUpdate: ((id: string, data: Partial<SettlementClientData>) => void) | null = null;

  // Tutorial v2: in-world Aura beacons. Usually a single one in the Academy.
  public onTutorialBeaconAdd: ((b: { id: string; name: string; x: number; y: number; radius: number }) => void) | null = null;
  public onTutorialBeaconRemove: ((id: string) => void) | null = null;
  public onSettlementRemove: ((id: string) => void) | null = null;
  public onSettlementHit: ((data: { settlementId: string; damage: number; blocked: boolean; destroyed: boolean; coreHull: number; coreMaxHull: number; x: number; y: number }) => void) | null = null;
  public onSettlementDestroyed: ((data: { settlementId: string; x: number; y: number }) => void) | null = null;
  public onAsteroidZones: ((zones: Array<{ id: string; x: number; y: number; radius: number; name?: string }>) => void) | null = null;

  // Debug: resource zone locations (asteroid/ice/wreckage hotspots) for constellation map overlay
  public onResourceZones: ((zones: Array<{ id: string; x: number; y: number; radius: number; kind: string }>) => void) | null = null;

  // Planet IDs where the local player has outposts (always visible on constellation map)
  public outpostPlanetIds: Set<string> = new Set();

  // Remote player projectile events (for visual rendering of other players' shots)
  public onPlayerProjectile: ((data: { playerId: string; x: number; y: number; angle: number; damage: number; speed: number; weaponType: 'laser' | 'kinetic'; color: number }) => void) | null = null;

  // Drone projectile events (for visual rendering of drone shots)
  public onDroneProjectile: ((data: { droneId: string; x: number; y: number; angle: number; damage: number; speed: number; weaponType: 'laser' | 'kinetic'; color: number }) => void) | null = null;

  // Player combat events (server-authoritative)
  public onPlayerHit: ((data: { playerId: string; damage: number; hitAngle: number; shieldDamage: number; hullDamage: number; x: number; y: number; isCollision?: boolean; velocityX?: number; velocityY?: number }) => void) | null = null;
  public onPlayerDestroyed: ((data: { playerId: string; x: number; y: number; rotation: number; shipClassId: string; nearestStationId: string; nearestStationName: string; threatLevel: string; itemsDropped: string[] }) => void) | null = null;
  public onShipLost: ((data: { playerId: string; x: number; y: number; rotation: number; lostShipClass: string; nearestStationId: string; nearestStationName: string; threatLevel: string; itemsDropped: string[] }) => void) | null = null;

  // Local player state changes (server-authoritative hull/shield/stun sync)
  public onLocalPlayerStateChange: ((data: { hull: number; maxHull: number; shield: number; maxShield: number; isStunned: boolean; stunEndTime: number; lastProcessedInput: number; currentHeat: number; maxHeat: number; isShipOverheated: boolean; shieldActive: boolean; powerCapacity: number; shieldRaisedAt: number; shieldRadius: number; shieldExpansionMs: number; isVenting: boolean; absorbedHeat: number }) => void) | null = null;

  // Local player ship class change (when switching ships)
  public onLocalPlayerShipClassChange: ((data: { shipClassId: string; shipId: string }) => void) | null = null;

  // Local player cloak state changes
  public onLocalPlayerCloakChange: ((data: { isCloaked: boolean; cloakExpiresAt: number }) => void) | null = null;

  // Local player combat state changes
  public onLocalPlayerCombatChange: ((data: { lastCombatTime: number }) => void) | null = null;

  // Local player reputation changes (synced from PlayerState)
  public onLocalPlayerReputationChange: ((data: { reputation: number }) => void) | null = null;

  public onLocalPlayerEquipmentChange: ((data: { slots: import('@astra/shared').SlotDefinition[] }) => void) | null = null;

  // Asteroid destroyed event (with loot info)
  public onAsteroidDestroyed: ((data: { asteroidId: string; resourceType: string; resourceAmount: number; x: number; y: number }) => void) | null = null;

  // Drop events
  public onDropAdd: ((drop: DropData) => void) | null = null;
  public onDropRemove: ((dropId: string) => void) | null = null;
  public onDropUpdate: ((dropId: string, drop: DropData) => void) | null = null;
  public onDropTransferResult: ((data: { dropId: string; success: boolean; taken?: number; transfers?: any[]; error?: string; itemType?: string; itemId?: string; all?: boolean; partial?: boolean }) => void) | null = null;
  public onShipCargoSnapshot: ((data: ShipCargoSnapshot) => void) | null = null;

  // Drone events
  public onDroneAdd: ((drone: DroneData) => void) | null = null;
  public onDroneRemove: ((droneId: string) => void) | null = null;
  public onDroneUpdate: ((droneId: string, data: Partial<DroneData>) => void) | null = null;
  public onDroneHit: ((data: { droneId: string; damage: number; hitAngle: number; destroyed: boolean; x: number; y: number }) => void) | null = null;
  public onDroneDestroyed: ((data: { droneId: string; x: number; y: number; droneType: string; killerSessionId: string }) => void) | null = null;
  public onPurchaseDroneResult: ((data: { success: boolean; error?: string; droneId?: string }) => void) | null = null;
  public onPurchaseShipDroneResult: ((data: { success: boolean; error?: string; droneId?: string }) => void) | null = null;
  public onDeployDroneResult: ((data: { droneId: string; success: boolean; error?: string }) => void) | null = null;
  public onRecallDroneResult: ((data: { droneId: string; success: boolean; error?: string }) => void) | null = null;
  public onShipDronesList: ((data: { shipId: string; drones: Array<{
    id: string; droneType: string; isDeployed: boolean; isRogue: boolean;
    constellationId: string; hull: number; maxHull: number;
    level: number; xp: number; xpToNext: number;
  }> }) => void) | null = null;

  // Local player credits sync
  public onCreditsChange: ((credits: number) => void) | null = null;

  // World object events - Suns
  public onSunAdd: ((sun: SunData) => void) | null = null;
  public onSunRemove: ((sunId: string) => void) | null = null;

  // World object events - Nebulae
  public onNebulaAdd: ((nebula: NebulaData) => void) | null = null;
  public onNebulaRemove: ((nebulaId: string) => void) | null = null;

  // Gateway events
  public onGatewayAdd: ((gateway: GatewayData) => void) | null = null;
  public onGatewayRemove: ((gatewayId: string) => void) | null = null;
  public onGatewayTravelStart: ((data: { targetConstellationX: number; targetConstellationY: number; targetConstellationName: string; spawnX: number; spawnY: number }) => void) | null = null;
  public onGatewayTravelError: ((data: { error: string }) => void) | null = null;

  // Resource harvesting events
  public onResourceHarvested: ((data: { playerId: string; resourceType: string; amount: number; source: string }) => void) | null = null;
  // Radiation damage from suns (bypasses shields, direct hull damage)
  public onRadiationDamage: ((data: { entityId: string; entityType: string; damage: number; sunId: string; proximity: number }) => void) | null = null;

  // Docking events
  public onDockingStarted: ((data: {
    stationId: string;
    stationName: string;
    dockPosition: number;
    targetX: number;
    targetY: number;
    targetRotation: number;
    animationDuration: number;
  }) => void) | null = null;
  public onDocked: ((data: { stationId: string; stationName: string; dockPosition: number; x: number; y: number }) => void) | null = null;
  public onUndocked: ((data: { x: number; y: number; velocityX: number; velocityY: number }) => void) | null = null;

  // DEV: Teleport result (snap position)
  public onDevTeleported: ((data: { x: number; y: number }) => void) | null = null;

  // Drone bay toggle result
  public onToggleShipDronesResult: ((data: { action: string; count: number; error?: string }) => void) | null = null;

  // Mining events
  public onMiningStarted: ((data: {
    asteroidId: string;
    asteroidX: number;
    asteroidY: number;
    asteroidRadius: number;
    miningSlot: number;
    resourceType: string;
    maxHealth: number;
    currentHealth: number;
    kind?: string;
  }) => void) | null = null;
  public onMiningStopped: ((data: { asteroidId?: string }) => void) | null = null;
  public onMiningTick: ((data: {
    asteroidId: string;
    damage: number;
    remainingHealth: number;
    maxHealth: number;
    resourcesGained: number;
    totalResourcesGained: number;
    fractionalProgress: number;
    pendingProgress: number;
    resourceType: string;
  }) => void) | null = null;
  public onAsteroidExploded: ((data: {
    asteroidId: string;
    pushVelocityX: number;
    pushVelocityY: number;
  }) => void) | null = null;
  public onMiningFailed: ((data: { reason: string }) => void) | null = null;

  // Remote player mining (for visual effects)
  public onPlayerMiningStarted: ((data: { playerId: string; asteroidId: string; miningSlot: number }) => void) | null = null;
  public onPlayerMiningStopped: ((data: { playerId: string; asteroidId: string }) => void) | null = null;

  // Station services (repairs only - buy/sell removed)
  public onStationShops: ((data: { shops: ShopData[] }) => void) | null = null;
  public onRepairResult: ((data: { success: boolean; error?: string; newCredits?: number; newHull?: number }) => void) | null = null;
  public onRepairCost: ((data: { currentHull: number; maxHull: number; cost: number; needsRepair: boolean }) => void) | null = null;

  // Order Book Market events (NEW - replaces NPC shop buy/sell)
  public onOrderBookData: ((data: {
    orderBook: import('@astra/shared').OrderBook;
    stationId: string;
    itemType: string;
    itemId: string;
  }) => void) | null = null;
  public onStationMarketsData: ((data: {
    markets: import('@astra/shared').MarketItemSummary[];
    stationId: string;
  }) => void) | null = null;
  public onOrderResult: ((data: {
    success: boolean;
    order?: import('@astra/shared').MarketOrderData;
    fills?: Array<{ orderId: string; quantity: number; pricePerUnit: number; totalPrice: number; fee: number }>;
    listingFee?: number;
    error?: string;
  }) => void) | null = null;
  public onMarketOrderResult: ((data: {
    success: boolean;
    fills: Array<{ orderId: string; quantity: number; pricePerUnit: number; totalPrice: number; fee: number }>;
    unfilledQuantity: number;
    totalCost: number;
    totalFees: number;
    error?: string;
    sentToBank?: boolean;
  }) => void) | null = null;
  public onCancelResult: ((data: {
    success: boolean;
    error?: string;
    refundedItems?: { itemId: string; quantity: number };
    refundedCredits?: number;
  }) => void) | null = null;
  public onMyOrdersData: ((data: { orders: import('@astra/shared').MarketOrderData[] }) => void) | null = null;
  public onPlayerInventoryData: ((data: { items: import('@astra/shared').InventoryItem[]; cargoUsed?: number; cargoCapacity?: number; launderingRate?: number; isSettlement?: boolean }) => void) | null = null;
  public onMarketHistoryData: ((data: {
    history: import('@astra/shared').MarketTransaction[];
    stationId: string;
    itemType: string;
    itemId: string;
  }) => void) | null = null;
  // Market Maker prices and availability
  public onMarketMakerPricesData: ((data: {
    stationId: string;
    itemId: string;
    buyPrice?: number;
    sellPrice?: number;
    spread?: number;
    activityLevel?: import('@astra/shared').MMActivityLevel;
    buyLimitRemaining?: number;
    sellLimitRemaining?: number;
    supported: boolean;
  }) => void) | null = null;
  // Real-time order book update notification (broadcast when any order changes)
  public onOrderBookUpdated: ((data: {
    stationId: string;
    itemType: string;
    itemId: string;
  }) => void) | null = null;

  // DEPRECATED: Old NPC shop callbacks (kept for compatibility, will be removed)
  public onShopInventory: ((data: { items: ShopInventoryItem[]; constellationEconomy?: ConstellationEconomyData; cargoUsed?: number; cargoCapacity?: number; error?: string }) => void) | null = null;
  public onSellableItems: ((data: { items: SellableItemData[]; constellationEconomy?: ConstellationEconomyData; error?: string }) => void) | null = null;
  public onAllSellableItems: ((data: { items: SellableItemData[]; constellationEconomy?: ConstellationEconomyData; source?: 'bank' | 'cargo'; error?: string }) => void) | null = null;
  public onShopTransactionResult: ((data: { success: boolean; error?: string; newCredits?: number }) => void) | null = null;

  // Inventory management events
  public onInventoryData: ((data: InventoryData) => void) | null = null;
  public onEquipResult: ((data: EquipResult) => void) | null = null;
  public onUnequipResult: ((data: UnequipResult) => void) | null = null;
  public onDiscardResult: ((data: DiscardResult) => void) | null = null;

  // Bank storage events
  public onPlayerBank: ((data: { items: BankItemData[]; error?: string }) => void) | null = null;
  public onRefineResult: ((data: { success: boolean; error?: string; outputId?: string; outputQty?: number; feeCharged?: number; runs?: number }) => void) | null = null;
  public onCraftResult: ((data: { success: boolean; error?: string; outputId?: string; outputQty?: number; feeCharged?: number; runs?: number }) => void) | null = null;
  public onShopsData: ((data: { stationId: string; shops: Array<{ category: string; type: string; ownerType: string; ownedByMe: boolean; feePct: number }> }) => void) | null = null;
  public onShopActionResult: ((data: { success: boolean; error?: string; action?: string; category?: string }) => void) | null = null;
  public onAuctionsData: ((data: { stationId: string; auctions: Array<{ category: string; basePrice: number; closesAt: string; highBid: number }> }) => void) | null = null;
  public onBidResult: ((data: { success: boolean; error?: string; category?: string; closesAt?: string; highBid?: number; basePrice?: number }) => void) | null = null;
  public onBankTransferResult: ((data: BankTransferResult) => void) | null = null;

  // Station UI - Player ships
  public onPlayerShips: ((data: { ships: PlayerShipData[]; currentShipId: string; credits: number }) => void) | null = null;
  public onShipSwitchResult: ((data: { success: boolean; error?: string; newShipId?: string }) => void) | null = null;
  public onShipRenameResult: ((data: { success: boolean; error?: string; shipId?: string; newName?: string }) => void) | null = null;
  public onAllShopItems: ((data: { items: ShopInventoryItem[]; constellationEconomy?: ConstellationEconomyData; cargoUsed?: number; cargoCapacity?: number; error?: string }) => void) | null = null;

  // Chat events
  public onChatMessage: ((data: ChatMessageData) => void) | null = null;

  // Universe map events
  public onUniverseMapData: ((data: UniverseMapData) => void) | null = null;
  public onConstellationPreviewData: ((data: ConstellationPreviewData) => void) | null = null;

  // Warp travel events
  public onWarpStateChanged: ((data: import('@astra/shared').WarpStateChangeMessage) => void) | null = null;
  public onWarpInterrupted: ((data: { reason: import('@astra/shared').WarpInterruptReason; attackerId?: string; attackerName?: string; beaconId?: string }) => void) | null = null;
  public onBeaconDeployed: ((data: { beaconId: string; x: number; y: number; radius: number; expiresAt: number }) => void) | null = null;
  public onBeaconDeployError: ((data: { error: string }) => void) | null = null;

  // Gateway cloak events
  public onCloakBroken: ((data: { reason: 'expired' | 'fired' | 'collision' }) => void) | null = null;

  // New player protection events
  public onTargetProtected: ((data: { message: string; targetId: string }) => void) | null = null;
  public onProtectionLost: ((data: { message: string; expiresAt: number }) => void) | null = null;
  public onEmergencyShipProvided: ((data: { message: string; shipClassId: string }) => void) | null = null;

  // Interdiction beacon events (world state)
  public onInterdictionBeaconAdd: ((beacon: import('@astra/shared').InterdictionBeaconData) => void) | null = null;
  public onInterdictionBeaconRemove: ((beaconId: string) => void) | null = null;
  public onInterdictionBeaconUpdate: ((beaconId: string, data: Partial<import('@astra/shared').InterdictionBeaconData>) => void) | null = null;

  // Bounty system events
  public onBountyListUpdate: ((data: { bounties: import('@astra/shared').BountyInfo[]; timestamp: number }) => void) | null = null;
  public onBountyClaimed: ((data: { amount: number; repGain: number; targetName: string }) => void) | null = null;
  public onBountyCleared: ((data: { playerId: string; playerName: string }) => void) | null = null;

  constructor() {
    // Use the server URL from authService (dynamic based on server selection)
    const wsUrl = authService.getColyseusUrl();
    this.client = new Colyseus.Client(wsUrl);
  }

  // Recreate the Colyseus client with a new server URL (called after server selection)
  setServerUrl(url: string): void {
    this.client = new Colyseus.Client(url);
  }

  async connect(constellationX: number = 0, constellationY: number = 0): Promise<boolean> {
    // Store for reconnection
    this.lastConstellationX = constellationX;
    this.lastConstellationY = constellationY;

    // Get auth token - authentication required
    const accessToken = authService.getAccessToken();
    const characterId = authService.selectedCharacterId;

    if (!accessToken) {
      console.error('No access token available - authentication required');
      return false;
    }

    if (!characterId) {
      console.error('No character selected - character selection required');
      return false;
    }

    // Ensure Colyseus client points to the selected server
    const serverUrl = authService.getColyseusUrl();
    this.setServerUrl(serverUrl);

    try {
      this.room = await this.client.joinOrCreate('constellation', {
        accessToken,
        characterId,
        constellationX,
        constellationY,
      });

      this.setupStateListeners();

      // Reset reconnection state on successful connect
      this.reconnectAttempts = 0;
      this.isReconnecting = false;
      this.isIntentionalDisconnect = false; // Allow auto-reconnect now that we're in a constellation

      if (this.onConnected) {
        this.onConnected();
      }

      return true;
    } catch (error) {
      console.error('Failed to connect to server:', error);
      if (this.onError) {
        this.onError(error as Error);
      }
      return false;
    }
  }

  private async attemptReconnect(): Promise<void> {
    if (this.isReconnecting) return;

    this.isReconnecting = true;
    this.reconnectAttempts++;

    if (this.onReconnecting) {
      this.onReconnecting(this.reconnectAttempts, this.MAX_RECONNECT_ATTEMPTS);
    }

    const success = await this.connect(this.lastConstellationX, this.lastConstellationY);

    if (!success && this.reconnectAttempts < this.MAX_RECONNECT_ATTEMPTS) {
      // Schedule next reconnect attempt
      this.isReconnecting = false;
      this.reconnectTimeout = setTimeout(() => {
        this.attemptReconnect();
      }, this.RECONNECT_DELAY * this.reconnectAttempts); // Exponential backoff
    } else if (!success) {
      // Max attempts reached
      console.error('Max reconnection attempts reached');
      this.isReconnecting = false;
      if (this.onReconnectFailed) {
        this.onReconnectFailed();
      }
    }
  }

  cancelReconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    this.isReconnecting = false;
    this.reconnectAttempts = 0;
  }

  private setupStateListeners(): void {
    if (!this.room) return;

    // Set up ping/pong RTT measurement
    this.startPingLoop();
    this.room.onMessage('pong', (data: { t: number }) => {
      this.rtt = Date.now() - data.t;
      // Exponential moving average (alpha = 0.2)
      this.rttSmoothed = this.rttSmoothed === 0
        ? this.rtt
        : this.rttSmoothed * 0.8 + this.rtt * 0.2;
    });

    // Tutorial: server pushes step messages → React panel renders them.
    // We cache the latest state on the NetworkManager because the panel mounts
    // AFTER `gameReady` fires (i.e. after appState flips to 'playing'), but
    // the server sends tutorial_state DURING onJoin — much earlier. Without
    // the cache, mitt drops the event because no listener exists yet.
    // Panel reads this cache on mount, then subscribes to future events.
    this.room.onMessage('tutorial_state', (data: { step: number; title: string; body: string; totalSteps: number; mission: 'mine' | 'kill' | null }) => {
      this.latestTutorialState = data;
      emitGameEvent('tutorialState', data);
    });
    this.room.onMessage('tutorial_mission_incomplete', (data: { step: number; mission: 'mine' | 'kill' }) => {
      emitGameEvent('tutorialMissionIncomplete', data);
    });
    this.room.onMessage('tutorial_mission_progress', (data: { step: number; mission: 'mine' | 'kill'; progress: number; threshold: number; complete: boolean }) => {
      this.latestTutorialMissionProgress = data;
      emitGameEvent('tutorialMissionProgress', data);
    });
    this.room.onMessage('tutorial_complete', () => {
      this.latestTutorialState = null;
      this.latestTutorialMissionProgress = null;
      emitGameEvent('tutorialComplete', {} as Record<string, never>);
    });
    // Post-tutorial one-shot welcome modal.
    this.room.onMessage('welcome_to_live_space', () => {
      emitGameEvent('welcomeToLiveSpace', {} as Record<string, never>);
    });

    // Anti-cheat (Layer A): server rejected a movement input that exceeded
    // the physical-max ceiling. Bridge to the game event bus so the local
    // Player can snap its prediction back to the authoritative position.
    this.room.onMessage('positionCorrection', (data: {
      x: number;
      y: number;
      rotation: number;
      velocityX: number;
      velocityY: number;
      reason: string;
    }) => {
      emitGameEvent('positionCorrection', data);
    });

    // Use getStateCallbacks for Colyseus 0.16+ API
    const $ = getStateCallbacks(this.room);

    // Listen for player additions
    $(this.room.state).players.onAdd((player: any, sessionId: string) => {
      console.log('[DEBUG] players.onAdd fired', { sessionId, username: player.username, isLocal: sessionId === this.room?.sessionId });
      // Handle local player separately
      if (sessionId === this.room?.sessionId) {
        if (this.onLocalPlayerLoaded) {
          this.onLocalPlayerLoaded(this.extractPlayerData(player, sessionId));
        }

        // Track previous shipClassId to detect changes
        let lastShipClassId = player.shipClassId;

        // Track equipment changes via a dirty flag instead of JSON.stringify on every state change.
        // Slots only change when equipping/unequipping at stations — very rare vs 20+ onChange/s during movement.
        // IMPORTANT: Must use $(parent).property.onAdd() pattern, NOT $(child).onAdd() — Colyseus 0.17 API.
        let equipmentDirty = false;
        if (player.slots) {
          $(player).slots.onAdd((slot: any) => {
            equipmentDirty = true;
            $(slot).onChange(() => { equipmentDirty = true; });
          });
          $(player).slots.onRemove(() => { equipmentDirty = true; });
          // Also listen to existing slots (already present at load time)
          player.slots.forEach((slot: any) => {
            $(slot).onChange(() => { equipmentDirty = true; });
          });
        }

        // Listen for local player's state changes (hull/shield/stun/credits from server)
        $(player).onChange(() => {
          if (this.onLocalPlayerStateChange) {
            this.onLocalPlayerStateChange({
              hull: player.hull,
              maxHull: player.maxHull,
              shield: player.shield,
              maxShield: player.maxShield,
              isStunned: player.isStunned,
              stunEndTime: player.stunEndTime,
              lastProcessedInput: player.lastProcessedInput,
              currentHeat: (player as any).currentHeat ?? 0,
              maxHeat: (player as any).maxHeat ?? 100,
              isShipOverheated: (player as any).isOverheated ?? false,
              shieldActive: (player as any).shieldActive ?? false,
              powerCapacity: (player as any).powerCapacity ?? 0,
              shieldRaisedAt: (player as any).shieldRaisedAt ?? 0,
              shieldRadius: (player as any).shieldRadius ?? 80,
              shieldExpansionMs: (player as any).shieldExpansionMs ?? 300,
              isVenting: (player as any).isVenting ?? false,
              absorbedHeat: (player as any).absorbedHeat ?? 0,
            });
          }
          if (this.onCreditsChange) {
            this.onCreditsChange(player.credits);
          }
          // Cloak state changes
          if (this.onLocalPlayerCloakChange) {
            this.onLocalPlayerCloakChange({
              isCloaked: player.isCloaked,
              cloakExpiresAt: player.cloakExpiresAt,
            });
          }
          // Combat state changes
          if (this.onLocalPlayerCombatChange) {
            this.onLocalPlayerCombatChange({
              lastCombatTime: player.lastCombatTime,
            });
          }
          // Reputation changes (synced from PlayerState)
          if (this.onLocalPlayerReputationChange) {
            this.onLocalPlayerReputationChange({
              reputation: player.reputation,
            });
          }
          // Ship class changes (when switching ships at shipyard)
          if (player.shipClassId !== lastShipClassId) {
            lastShipClassId = player.shipClassId;
            if (this.onLocalPlayerShipClassChange) {
              this.onLocalPlayerShipClassChange({
                shipClassId: player.shipClassId,
                shipId: player.shipId,
              });
            }
          }
          // Equipment slot changes — only fire when slots actually changed (dirty flag)
          if (equipmentDirty) {
            equipmentDirty = false;
            if (this.onLocalPlayerEquipmentChange) {
              const slots = this.extractPlayerData(player, sessionId).slots;
              if (slots) {
                this.onLocalPlayerEquipmentChange({ slots });
              }
            }
          }
        });
        return;
      }

      if (this.onPlayerJoin) {
        this.onPlayerJoin(this.extractPlayerData(player, sessionId));
      }

      // Listen for this player's changes
      $(player).onChange(() => {
        if (this.onPlayerUpdate) {
          this.onPlayerUpdate(sessionId, this.extractPlayerData(player, sessionId));
        }
      });
    });

    // Listen for player removals
    $(this.room.state).players.onRemove((_player: any, sessionId: string) => {
      console.log('[DEBUG] players.onRemove fired', { sessionId });
      if (this.onPlayerLeave) {
        this.onPlayerLeave(sessionId);
      }
    });

    // Listen for asteroid additions
    $(this.room.state).asteroids.onAdd((asteroid: any, asteroidId: string) => {
      if (this.onAsteroidAdd) {
        this.onAsteroidAdd(this.extractAsteroidData(asteroid, asteroidId));
      }

      // Listen for asteroid changes (health updates during mining)
      $(asteroid).onChange(() => {
        if (this.onAsteroidUpdate) {
          this.onAsteroidUpdate(asteroidId, this.extractAsteroidData(asteroid, asteroidId));
        }
      });
    });

    // Listen for asteroid removals (mined)
    $(this.room.state).asteroids.onRemove((_asteroid: any, asteroidId: string) => {
      if (this.onAsteroidRemove) {
        this.onAsteroidRemove(asteroidId);
      }
    });

    // V1: harvestable listeners (ice asteroids / gas pockets / wreckages)
    // Each follows the asteroid pattern: onAdd subscribes to onChange for health updates.
    $(this.room.state).iceAsteroids.onAdd((entity: any, id: string) => {
      if (this.onIceAsteroidAdd) this.onIceAsteroidAdd(this.extractHarvestableData(entity, id));
      $(entity).onChange(() => {
        if (this.onIceAsteroidUpdate) this.onIceAsteroidUpdate(id, this.extractHarvestableData(entity, id));
      });
    });
    $(this.room.state).iceAsteroids.onRemove((_entity: any, id: string) => {
      if (this.onIceAsteroidRemove) this.onIceAsteroidRemove(id);
    });

    $(this.room.state).gasPockets.onAdd((entity: any, id: string) => {
      if (this.onGasPocketAdd) this.onGasPocketAdd(this.extractHarvestableData(entity, id));
      $(entity).onChange(() => {
        if (this.onGasPocketUpdate) this.onGasPocketUpdate(id, this.extractHarvestableData(entity, id));
      });
    });
    $(this.room.state).gasPockets.onRemove((_entity: any, id: string) => {
      if (this.onGasPocketRemove) this.onGasPocketRemove(id);
    });

    $(this.room.state).wreckages.onAdd((entity: any, id: string) => {
      if (this.onWreckageAdd) this.onWreckageAdd(this.extractHarvestableData(entity, id));
      $(entity).onChange(() => {
        if (this.onWreckageUpdate) this.onWreckageUpdate(id, this.extractHarvestableData(entity, id));
      });
    });
    $(this.room.state).wreckages.onRemove((_entity: any, id: string) => {
      if (this.onWreckageRemove) this.onWreckageRemove(id);
    });

    // Listen for solar systems (for constellation map display)
    $(this.room.state).systems.onAdd((system: any, systemId: string) => {
      if (this.onSystemAdd) {
        this.onSystemAdd({
          id: systemId,
          name: system.name,
          x: system.x,
          y: system.y,
          starType: system.starType,
          sunRadius: system.sunRadius,
        });
      }
    });

    // Listen for system removals
    $(this.room.state).systems.onRemove((_system: any, systemId: string) => {
      if (this.onSystemRemove) {
        this.onSystemRemove(systemId);
      }
    });

    // Listen for planets
    $(this.room.state).planets.onAdd((planet: any, planetId: string) => {
      if (this.onPlanetAdd) {
        this.onPlanetAdd({
          id: planetId,
          name: planet.name,
          systemId: planet.systemId,
          x: planet.x,
          y: planet.y,
          radius: planet.radius,
          planetType: planet.planetType,
          orbitalDistance: planet.orbitalDistance,
          orbitalSpeed: planet.orbitalSpeed,
          orbitalAngle: planet.orbitalAngle,
          profileSeed: planet.profileSeed,
          clusterDensity: planet.clusterDensity,
          sizeMultiplier: planet.sizeMultiplier ?? 1.0,
        });
      }

      // Listen for planet position changes (orbital movement)
      $(planet).onChange(() => {
        if (this.onPlanetChange) {
          this.onPlanetChange(planetId, { x: planet.x, y: planet.y });
        }
      });
    });

    // Listen for stations
    $(this.room.state).stations.onAdd((station: any, stationId: string) => {
      if (this.onStationAdd) {
        this.onStationAdd({
          id: stationId,
          name: station.name,
          x: station.x,
          y: station.y,
        });
      }
    });

    // Listen for private stations (player-owned outposts)
    $(this.room.state).privateStations.onAdd((ps: any, psId: string) => {
      if (this.onPrivateStationAdd) {
        this.onPrivateStationAdd({
          id: psId,
          ownerPlayerId: ps.ownerPlayerId,
          ownerName: ps.ownerName,
          name: ps.name,
          x: ps.x,
          y: ps.y,
          tier: ps.tier,
          status: ps.status,
          droneCapacity: ps.droneCapacity,
        });
      }
      $(ps).onChange(() => {
        if (this.onPrivateStationUpdate) {
          this.onPrivateStationUpdate(psId, {
            name: ps.name,
            tier: ps.tier,
            status: ps.status,
            droneCapacity: ps.droneCapacity,
            x: ps.x,
            y: ps.y,
          });
        }
      });
    });
    $(this.room.state).privateStations.onRemove((_ps: any, psId: string) => {
      if (this.onPrivateStationRemove) this.onPrivateStationRemove(psId);
    });

    // Settlements (emergent rogue-drone nests). AOI-culled server-side, so onAdd
    // fires when the local player gets close enough to one to discover it.
    $(this.room.state).settlements.onAdd((s: any, sid: string) => {
      if (this.onSettlementAdd) {
        this.onSettlementAdd({
          id: sid, name: s.name, x: s.x, y: s.y, tier: s.tier,
          coreHull: s.coreHull, coreMaxHull: s.coreMaxHull, coreExposed: s.coreExposed,
        });
      }
      $(s).onChange(() => {
        if (this.onSettlementUpdate) {
          this.onSettlementUpdate(sid, {
            x: s.x, y: s.y, coreHull: s.coreHull, coreMaxHull: s.coreMaxHull, coreExposed: s.coreExposed,
          });
        }
      });
    });
    $(this.room.state).settlements.onRemove((_s: any, sid: string) => {
      if (this.onSettlementRemove) this.onSettlementRemove(sid);
    });

    // Tutorial v2 beacons — fire-and-forget callbacks. Static (no position
    // updates), so only Add/Remove are wired. The proxy property is always
    // present (declared in GameState), so no optional chaining — that would
    // silently swallow registration and the modal would never appear.
    $(this.room.state).tutorialBeacons.onAdd((b: any, bid: string) => {
      this.onTutorialBeaconAdd?.({ id: bid, name: b.name, x: b.x, y: b.y, radius: b.radius });
    });
    $(this.room.state).tutorialBeacons.onRemove((_b: any, bid: string) => {
      this.onTutorialBeaconRemove?.(bid);
    });

    // Listen for drop additions
    $(this.room.state).drops.onAdd((drop: any, dropId: string) => {
      if (this.onDropAdd) {
        this.onDropAdd(this.extractDropData(drop, dropId));
      }
      $(drop).onChange(() => {
        if (this.onDropUpdate) this.onDropUpdate(dropId, this.extractDropData(drop, dropId));
      });
    });

    // Listen for drop removals (expired or fully looted)
    $(this.room.state).drops.onRemove((_drop: any, dropId: string) => {
      if (this.onDropRemove) {
        this.onDropRemove(dropId);
      }
    });

    this.room.onMessage('dropTransferResult', (data: any) => {
      if (this.onDropTransferResult) this.onDropTransferResult(data);
    });

    this.room.onMessage('shipCargoSnapshot', (data: any) => {
      if (this.onShipCargoSnapshot) this.onShipCargoSnapshot(data);
    });

    this.room.onMessage('refineResult', (data: any) => {
      if (this.onRefineResult) this.onRefineResult(data);
    });

    this.room.onMessage('craftResult', (data: any) => {
      if (this.onCraftResult) this.onCraftResult(data);
    });

    this.room.onMessage('shopsData', (data: any) => {
      if (this.onShopsData) this.onShopsData(data);
    });

    this.room.onMessage('shopActionResult', (data: any) => {
      if (this.onShopActionResult) this.onShopActionResult(data);
    });

    this.room.onMessage('auctionsData', (data: any) => {
      if (this.onAuctionsData) this.onAuctionsData(data);
    });

    this.room.onMessage('bidResult', (data: any) => {
      if (this.onBidResult) this.onBidResult(data);
    });

    // Drones
    $(this.room.state).drones.onAdd((drone: any, droneId: string) => {
      if (this.onDroneAdd) {
        this.onDroneAdd(this.extractDroneData(drone, droneId));
      }
      $(drone).onChange(() => {
        if (this.onDroneUpdate) {
          this.onDroneUpdate(droneId, this.extractDroneData(drone, droneId));
        }
      });
    });
    $(this.room.state).drones.onRemove((_drone: any, droneId: string) => {
      if (this.onDroneRemove) this.onDroneRemove(droneId);
    });

    this.room.onMessage('droneHit', (data: any) => {
      if (this.onDroneHit) this.onDroneHit(data);
    });

    this.room.onMessage('droneDestroyed', (data: any) => {
      if (this.onDroneDestroyed) this.onDroneDestroyed(data);
    });

    this.room.onMessage('settlementHit', (data: any) => {
      if (this.onSettlementHit) this.onSettlementHit(data);
    });
    this.room.onMessage('settlementDestroyed', (data: any) => {
      if (this.onSettlementDestroyed) this.onSettlementDestroyed(data);
    });

    this.room.onMessage('purchaseDroneResult', (data: any) => {
      if (this.onPurchaseDroneResult) this.onPurchaseDroneResult(data);
    });
    this.room.onMessage('purchaseShipDroneResult', (data: any) => {
      if (this.onPurchaseShipDroneResult) this.onPurchaseShipDroneResult(data);
    });
    this.room.onMessage('deployDroneResult', (data: any) => {
      if (this.onDeployDroneResult) this.onDeployDroneResult(data);
    });
    this.room.onMessage('recallDroneResult', (data: any) => {
      if (this.onRecallDroneResult) this.onRecallDroneResult(data);
    });
    this.room.onMessage('shipDronesList', (data: any) => {
      if (this.onShipDronesList) this.onShipDronesList(data);
    });

    // Listen for asteroid zones (sent on join for map display)
    this.room.onMessage('asteroidZones', (zones: any) => {
      if (this.onAsteroidZones) {
        this.onAsteroidZones(zones);
      }
    });

    // Debug: resource zone locations for constellation map overlay
    this.room.onMessage('resourceZones', (zones: any) => {
      if (this.onResourceZones) {
        this.onResourceZones(zones);
      }
    });

    // Planet IDs where the player has outposts — always visible on constellation map
    this.room.onMessage('playerOutpostPlanets', (data: { planetIds: string[] }) => {
      this.outpostPlanetIds = new Set(data.planetIds);
    });

    // Listen for asteroid destroyed events (for loot effects)
    this.room.onMessage('asteroidDestroyed', (data: any) => {
      if (this.onAsteroidDestroyed) {
        this.onAsteroidDestroyed(data);
      }
    });

    // Listen for player projectile events (other players shooting - visual only)
    this.room.onMessage('playerProjectile', (data: any) => {
      if (this.onPlayerProjectile) {
        this.onPlayerProjectile(data);
      }
    });

    this.room.onMessage('droneProjectile', (data: any) => {
      if (this.onDroneProjectile) {
        this.onDroneProjectile(data);
      }
    });

    // Acknowledge dev dummy spawn/despawn results (no UI action needed)
    this.room.onMessage('spawnDummyEnemyResult', () => {});
    this.room.onMessage('despawnDummyEnemiesResult', () => {});
    this.room.onMessage('devChangeShipResult', () => {});
    this.room.onMessage('toggleShipDronesResult', (data: any) => {
      if (this.onToggleShipDronesResult) this.onToggleShipDronesResult(data);
    });
    this.room.onMessage('devTeleportResult', (data: any) => {
      if (data.success && this.onDevTeleported) {
        this.onDevTeleported({ x: data.x, y: data.y });
      }
    });

    // Listen for player hit events (server-authoritative damage)
    this.room.onMessage('playerHit', (data: any) => {
      if (this.onPlayerHit) {
        this.onPlayerHit(data);
      }
    });

    // Listen for player destroyed events (Green/Yellow zone deaths - ship kept)
    this.room.onMessage('playerDestroyed', (data: any) => {
      if (this.onPlayerDestroyed) {
        this.onPlayerDestroyed(data);
      }
    });

    // Listen for ship lost events (Red zone deaths - ship deleted)
    this.room.onMessage('shipLost', (data: any) => {
      if (this.onShipLost) {
        this.onShipLost(data);
      }
    });

    // ==================== WORLD OBJECTS ====================

    // Listen for sun additions
    $(this.room.state).suns.onAdd((sun: any, sunId: string) => {
      if (this.onSunAdd) {
        this.onSunAdd(this.extractSunData(sun, sunId));
      }
    });

    // Listen for sun removals
    $(this.room.state).suns.onRemove((_sun: any, sunId: string) => {
      if (this.onSunRemove) {
        this.onSunRemove(sunId);
      }
    });

    // Listen for nebula additions
    $(this.room.state).nebulae.onAdd((nebula: any, nebulaId: string) => {
      if (this.onNebulaAdd) {
        this.onNebulaAdd(this.extractNebulaData(nebula, nebulaId));
      }
    });

    // Listen for nebula removals
    $(this.room.state).nebulae.onRemove((_nebula: any, nebulaId: string) => {
      if (this.onNebulaRemove) {
        this.onNebulaRemove(nebulaId);
      }
    });

    // Listen for gateway additions
    $(this.room.state).gateways.onAdd((gateway: any, gatewayId: string) => {
      if (this.onGatewayAdd) {
        this.onGatewayAdd(this.extractGatewayData(gateway, gatewayId));
      }
    });

    // Listen for gateway removals
    $(this.room.state).gateways.onRemove((_gateway: any, gatewayId: string) => {
      if (this.onGatewayRemove) {
        this.onGatewayRemove(gatewayId);
      }
    });

    // Listen for interdiction beacon additions
    $(this.room.state).interdictionBeacons.onAdd((beacon: any, beaconId: string) => {
      if (this.onInterdictionBeaconAdd) {
        this.onInterdictionBeaconAdd({
          id: beaconId,
          ownerId: beacon.ownerId,
          ownerName: beacon.ownerName,
          x: beacon.x,
          y: beacon.y,
          radius: beacon.radius,
          expiresAt: beacon.expiresAt,
          health: beacon.health,
          maxHealth: beacon.maxHealth,
        });
      }
    });

    // Listen for interdiction beacon removals
    $(this.room.state).interdictionBeacons.onRemove((_beacon: any, beaconId: string) => {
      if (this.onInterdictionBeaconRemove) {
        this.onInterdictionBeaconRemove(beaconId);
      }
    });

    // Listen for resource harvested events
    this.room.onMessage('resourceHarvested', (data: any) => {
      if (this.onResourceHarvested) {
        this.onResourceHarvested(data);
      }
    });

    // Listen for radiation damage events (sun damage bypasses shields)
    this.room.onMessage('radiationDamage', (data: any) => {
      if (this.onRadiationDamage) {
        this.onRadiationDamage(data);
      }
    });

    // Listen for docking events
    this.room.onMessage('dockingStarted', (data: any) => {
      if (this.onDockingStarted) {
        this.onDockingStarted(data);
      }
    });

    this.room.onMessage('docked', (data: any) => {
      if (this.onDocked) {
        this.onDocked(data);
      }
    });

    this.room.onMessage('undocked', (data: any) => {
      if (this.onUndocked) {
        this.onUndocked(data);
      }
    });

    // ==================== GATEWAY MESSAGE LISTENERS ====================

    this.room.onMessage('gateway_travel_start', (data: any) => {
      if (this.onGatewayTravelStart) {
        this.onGatewayTravelStart(data);
      }
    });

    this.room.onMessage('gateway_travel_error', (data: any) => {
      if (this.onGatewayTravelError) {
        this.onGatewayTravelError(data);
      }
    });

    // ==================== MINING MESSAGE LISTENERS ====================

    this.room.onMessage('miningStarted', (data: any) => {
      if (this.onMiningStarted) {
        this.onMiningStarted(data);
      }
    });

    this.room.onMessage('miningStopped', (data: any) => {
      if (this.onMiningStopped) {
        this.onMiningStopped(data || {});
      }
    });

    this.room.onMessage('miningTick', (data: any) => {
      if (this.onMiningTick) {
        this.onMiningTick(data);
      }
    });

    this.room.onMessage('asteroidExploded', (data: any) => {
      if (this.onAsteroidExploded) {
        this.onAsteroidExploded(data);
      }
    });

    this.room.onMessage('miningFailed', (data: any) => {
      if (this.onMiningFailed) {
        this.onMiningFailed(data);
      }
    });

    // Remote player mining (for visual effects on other clients)
    this.room.onMessage('playerMiningStarted', (data: any) => {
      if (this.onPlayerMiningStarted) {
        this.onPlayerMiningStarted(data);
      }
    });

    this.room.onMessage('playerMiningStopped', (data: any) => {
      if (this.onPlayerMiningStopped) {
        this.onPlayerMiningStopped(data);
      }
    });

    // ==================== STATION SERVICES (REPAIRS) ====================

    this.room.onMessage('stationShops', (data: any) => {
      if (this.onStationShops) {
        this.onStationShops(data);
      }
    });

    this.room.onMessage('repairResult', (data: any) => {
      if (this.onRepairResult) {
        this.onRepairResult(data);
      }
    });

    this.room.onMessage('repairCost', (data: any) => {
      if (this.onRepairCost) {
        this.onRepairCost(data);
      }
    });

    // ==================== ORDER BOOK MARKET MESSAGE LISTENERS ====================

    this.room.onMessage('orderBookData', (data: any) => {
      if (this.onOrderBookData) {
        this.onOrderBookData(data);
      }
    });

    this.room.onMessage('stationMarketsData', (data: any) => {
      if (this.onStationMarketsData) {
        this.onStationMarketsData(data);
      }
    });

    this.room.onMessage('orderResult', (data: any) => {
      if (this.onOrderResult) {
        this.onOrderResult(data);
      }
    });

    this.room.onMessage('marketOrderResult', (data: any) => {
      if (this.onMarketOrderResult) {
        this.onMarketOrderResult(data);
      }
    });

    this.room.onMessage('cancelResult', (data: any) => {
      if (this.onCancelResult) {
        this.onCancelResult(data);
      }
    });

    this.room.onMessage('myOrdersData', (data: any) => {
      if (this.onMyOrdersData) {
        this.onMyOrdersData(data);
      }
    });

    this.room.onMessage('playerInventoryData', (data: any) => {
      if (this.onPlayerInventoryData) {
        this.onPlayerInventoryData(data);
      }
    });

    this.room.onMessage('marketHistoryData', (data: any) => {
      if (this.onMarketHistoryData) {
        this.onMarketHistoryData(data);
      }
    });

    this.room.onMessage('marketMakerPricesData', (data: any) => {
      if (this.onMarketMakerPricesData) {
        this.onMarketMakerPricesData(data);
      }
    });

    // Real-time order book update broadcast (sent when any order changes in the room)
    this.room.onMessage('orderBookUpdated', (data: any) => {
      if (this.onOrderBookUpdated) {
        this.onOrderBookUpdated(data);
      }
    });

    // DEPRECATED: Old NPC shop message listeners (kept for compatibility)
    this.room.onMessage('shopInventory', (data: any) => {
      if (this.onShopInventory) {
        this.onShopInventory(data);
      }
    });

    this.room.onMessage('sellableItems', (data: any) => {
      if (this.onSellableItems) {
        this.onSellableItems(data);
      }
    });

    this.room.onMessage('allSellableItems', (data: any) => {
      if (this.onAllSellableItems) {
        this.onAllSellableItems(data);
      }
    });

    this.room.onMessage('shopTransactionResult', (data: any) => {
      if (this.onShopTransactionResult) {
        this.onShopTransactionResult(data);
      }
    });

    // Inventory management messages
    this.room.onMessage('inventoryData', (data: any) => {
      if (this.onInventoryData) {
        this.onInventoryData(data);
      }
    });

    this.room.onMessage('equipResult', (data: any) => {
      if (this.onEquipResult) {
        this.onEquipResult(data);
      }
    });

    this.room.onMessage('unequipResult', (data: any) => {
      if (this.onUnequipResult) {
        this.onUnequipResult(data);
      }
    });

    this.room.onMessage('discardResult', (data: any) => {
      if (this.onDiscardResult) {
        this.onDiscardResult(data);
      }
    });

    // Bank storage messages
    this.room.onMessage('playerBank', (data: any) => {
      if (this.onPlayerBank) {
        this.onPlayerBank(data);
      }
    });

    this.room.onMessage('bankTransferResult', (data: any) => {
      if (this.onBankTransferResult) {
        this.onBankTransferResult(data);
      }
    });

    // Station UI - Player ships
    this.room.onMessage('playerShips', (data: any) => {
      if (this.onPlayerShips) {
        this.onPlayerShips(data);
      }
    });

    this.room.onMessage('shipSwitchResult', (data: any) => {
      if (this.onShipSwitchResult) {
        this.onShipSwitchResult(data);
      }
    });

    this.room.onMessage('shipRenameResult', (data: any) => {
      if (this.onShipRenameResult) {
        this.onShipRenameResult(data);
      }
    });

    this.room.onMessage('allShopItems', (data: any) => {
      if (this.onAllShopItems) {
        this.onAllShopItems(data);
      }
    });

    // Chat messages
    this.room.onMessage('chatMessage', (data: ChatMessageData) => {
      if (this.onChatMessage) {
        this.onChatMessage(data);
      }
    });

    // Universe map data
    this.room.onMessage('universeMapData', (data: UniverseMapData) => {
      if (this.onUniverseMapData) {
        this.onUniverseMapData(data);
      }
    });

    // Per-constellation preview for the universe-map sidepanel.
    this.room.onMessage('constellationPreviewData', (data: ConstellationPreviewData) => {
      if (this.onConstellationPreviewData) {
        this.onConstellationPreviewData(data);
      }
    });

    // Warp travel messages
    this.room.onMessage('warpStateChanged', (data: any) => {
      if (this.onWarpStateChanged) {
        this.onWarpStateChanged(data);
      }
    });

    this.room.onMessage('warpInterrupted', (data: any) => {
      if (this.onWarpInterrupted) {
        this.onWarpInterrupted(data);
      }
    });

    this.room.onMessage('beaconDeployed', (data: any) => {
      if (this.onBeaconDeployed) {
        this.onBeaconDeployed(data);
      }
    });

    this.room.onMessage('disruptorActivated', (data: { duration: number; cooldown: number; range: number }) => {
      emitGameEvent('disruptorActivated', data);
    });

    this.room.onMessage('cargoScanResult', (data: any) => {
      emitGameEvent('cargoScanResult', data);
    });

    this.room.onMessage('inspectResult', (data: any) => {
      emitGameEvent('inspectResult', data);
    });

    // ==================== PLANET MANAGEMENT PANEL ====================
    this.room.onMessage('planet_panel_snapshot', (data: unknown) => {
      emitGameEvent('planetPanelSnapshot', data);
    });
    this.room.onMessage('planet_panel_error', (data: { reason: string }) => {
      emitGameEvent('planetPanelError', data);
    });
    this.room.onMessage('planet_panel_closed', () => {
      emitGameEvent('planetPanelClosed', {});
    });
    this.room.onMessage('structure_planted', (data: { structure: unknown }) => {
      emitGameEvent('planetStructurePlanted', data);
    });
    this.room.onMessage(
      'structure_dismantled',
      (data: { structureId: string; reason: 'manual' | 'auto_dismantle' }) => {
        emitGameEvent('planetStructureDismantled', data);
      }
    );
    this.room.onMessage('structure_depleted', (data: { structureId: string }) => {
      emitGameEvent('planetStructureDepleted', data);
    });
    this.room.onMessage('structure_toggled', (data: { structureId: string; isActive: boolean }) => {
      emitGameEvent('planetStructureToggled', data);
    });
    this.room.onMessage(
      'buffer_updated',
      (data: { outpostId: string; contents: Record<string, number> }) => {
        emitGameEvent('planetBufferUpdated', data);
      }
    );
    this.room.onMessage('activity_log_entry', (data: { entry: unknown }) => {
      emitGameEvent('planetActivityEntry', data);
    });
    this.room.onMessage('scan_details', (data: unknown) => {
      emitGameEvent('planetScanDetails', data);
    });
    this.room.onMessage('planet_panel_credits', (data: { credits: number }) => {
      emitGameEvent('planetPanelCredits', data);
    });
    this.room.onMessage('richness_diffs_updated', (data: { diffs: Array<{ cell_lat: number; cell_lon: number; depletion_organic: number; depletion_crystal: number }> }) => {
      emitGameEvent('planetRichnessDiffsUpdated', data);
    });
    this.room.onMessage('hex_state_updated', (data: { hexState: Array<{ hex_id: number; delta_organic: number; delta_crystal: number }> }) => {
      emitGameEvent('planetHexStateUpdated', data);
    });

    this.room.onMessage('beaconDeployError', (data: any) => {
      if (this.onBeaconDeployError) {
        this.onBeaconDeployError(data);
      }
    });

    // Handle cloak broken message
    this.room.onMessage('cloakBroken', (data: any) => {
      if (this.onCloakBroken) {
        this.onCloakBroken(data);
      }
    });

    // New player protection messages
    this.room.onMessage('target_protected', (data: any) => {
      // Target is protected - show notification
      if (this.onTargetProtected) {
        this.onTargetProtected(data);
      }
    });

    this.room.onMessage('protection_lost', (data: any) => {
      // Player lost protection by attacking first
      if (this.onProtectionLost) {
        this.onProtectionLost(data);
      }
    });

    this.room.onMessage('emergency_ship_provided', (data: any) => {
      // Safety net triggered - player got a free ship
      if (this.onEmergencyShipProvided) {
        this.onEmergencyShipProvided(data);
      }
    });

    // Reputation events
    this.room.onMessage('reputationSync', (data: any) => {
      emitGameEvent('reputationSync', data);
    });

    this.room.onMessage('reputationChange', (data: any) => {
      emitGameEvent('reputationChange', data);
    });

    this.room.onMessage('policeWarning', (data: any) => {
      emitGameEvent('policeWarning', data);
    });

    // Bounty system events
    this.room.onMessage('bountyListUpdate', (data: any) => {
      if (this.onBountyListUpdate) {
        this.onBountyListUpdate(data);
      }
      emitGameEvent('bountyListUpdate', data);
    });

    this.room.onMessage('bountyClaimed', (data: any) => {
      if (this.onBountyClaimed) {
        this.onBountyClaimed(data);
      }
      emitGameEvent('bountyClaimed', data);
    });

    this.room.onMessage('bountyCleared', (data: any) => {
      if (this.onBountyCleared) {
        this.onBountyCleared(data);
      }
      emitGameEvent('bountyCleared', data);
    });

    // Radar scan results
    this.room.onMessage('radar_entities', (data: any) => {
      emitGameEvent('radarEntitiesDiscovered', { entities: data.entities });
    });

    // Handle disconnection
    this.room.onLeave((code: number) => {
      this.room = null;

      if (this.onDisconnected) {
        this.onDisconnected(code);
      }

      // Skip auto-reconnect if this was an intentional disconnect (gateway travel)
      if (this.isIntentionalDisconnect) {
        return;
      }

      // Auto-reconnect for unexpected disconnections (codes 1000-1015 are normal/expected)
      // Code 1000 = normal closure, 1001 = going away, 4000+ = application errors
      // Don't reconnect if kicked for duplicate session (logged in elsewhere)
      const shouldNotReconnect = code === 1000 || code === 1001 || code === DISCONNECT_CODES.DUPLICATE_SESSION;
      if (!shouldNotReconnect && this.reconnectAttempts < this.MAX_RECONNECT_ATTEMPTS) {
        setTimeout(() => this.attemptReconnect(), this.RECONNECT_DELAY);
      }
    });

    // Handle errors
    this.room.onError((code: number, message?: string) => {
      console.error('Room error:', code, message);
      if (this.onError) {
        this.onError(new Error(`Room error ${code}: ${message}`));
      }
    });
  }

  private extractPlayerData(player: any, sessionId: string): RemotePlayerData {
    // Extract slots if available
    const slots: import('@astra/shared').SlotDefinition[] | undefined = player.slots
      ? Array.from(player.slots.values()).map((slot: any) => ({
          type: slot.slotType,
          size: slot.size,
          position: slot.position,
          equipped: slot.equipped || undefined,
        }))
      : undefined;

    return {
      id: player.id,
      sessionId,
      username: player.username,
      corpTag: player.corpTag ?? '',
      avatarUrl: player.avatarUrl,
      shipClassId: player.shipClassId,
      shipColor: player.shipColor ?? 0x20a5a6, // Default teal if not set
      x: player.x,
      y: player.y,
      rotation: player.rotation,
      velocityX: player.velocityX,
      velocityY: player.velocityY,
      thrusting: player.thrusting,
      hull: player.hull,
      maxHull: player.maxHull,
      shield: player.shield ?? 0,
      maxShield: player.maxShield ?? 0,
      totalPower: player.totalPower ?? 0,
      slots,
      // Docking state
      isDocked: player.isDocked,
      isDockingAnimation: player.isDockingAnimation,
      dockedStationId: player.dockedStationId,
      dockPosition: player.dockPosition,
      // Warp travel state
      warpPhase: player.warpPhase,
      warpTargetX: player.warpTargetX,
      warpTargetY: player.warpTargetY,
      warpSpeed: player.warpSpeed,
      // Combat-overhaul: natural shield state
      shieldActive: player.shieldActive ?? false,
      shieldRadius: player.shieldRadius ?? 80,
      shieldRaisedAt: player.shieldRaisedAt ?? 0,
      shieldExpansionMs: player.shieldExpansionMs ?? 300,
      isOverheated: player.isOverheated ?? false,
      isVenting: player.isVenting ?? false,
      // Stun state
      isStunned: player.isStunned ?? false,
      stunEndTime: player.stunEndTime ?? 0,
      // Boost state
      isBoosting: player.isBoosting ?? false,
      // Beam weapon state
      beamFiring: player.beamFiring ?? false,
      // DEV: dummy enemy flag
      isDummy: player.isDummy ?? false,
    };
  }

  private extractAsteroidData(asteroid: any, asteroidId: string): AsteroidData {
    return {
      id: asteroidId,
      x: asteroid.x,
      y: asteroid.y,
      size: asteroid.size,
      resourceType: asteroid.resourceType,
      resourceAmount: asteroid.resourceAmount,
      health: asteroid.health,
      maxHealth: asteroid.maxHealth,
    };
  }

  private extractHarvestableData(entity: any, id: string): HarvestableEntityData {
    return {
      id,
      x: entity.x,
      y: entity.y,
      size: entity.size,
      resourceType: entity.resourceType,
      resourceTier: entity.resourceTier ?? 1,
      resourceAmount: entity.resourceAmount,
      health: entity.health,
      maxHealth: entity.maxHealth,
      zoneId: entity.zoneId,
      nebulaId: entity.nebulaId,
    };
  }

  private extractDropData(drop: any, dropId: string): DropData {
    const items: DropItemEntry[] = [];
    if (drop.items) {
      for (const it of drop.items) {
        items.push({ itemType: it.itemType, itemId: it.itemId, quantity: it.quantity });
      }
    }

    return {
      id: dropId,
      x: drop.x,
      y: drop.y,
      items,
      ownerSessionId: drop.ownerSessionId || '',
      ownerUntil: drop.ownerUntil || 0,
      createdAt: drop.createdAt,
      expiresAt: drop.expiresAt,
    };
  }

  private extractDroneData(drone: any, droneId: string): DroneData {
    return {
      id: droneId,
      droneType: drone.droneType,
      ownerType: drone.ownerType,
      ownerId: drone.ownerId,
      homeStationId: drone.homeStationId,
      x: drone.x,
      y: drone.y,
      velocityX: drone.velocityX,
      velocityY: drone.velocityY,
      rotation: drone.rotation,
      hull: drone.hull,
      maxHull: drone.maxHull,
      shield: drone.shield,
      maxShield: drone.maxShield,
      state: drone.state,
      targetId: drone.targetId ?? '',
      warpPhase: drone.warpPhase ?? 'none',
      isRogue: drone.isRogue,
      shipDefId: drone.shipDefId ?? '',
      level: typeof drone.level === 'number' ? drone.level : 1,
    };
  }

  public sendPurchaseDrone(stationId: string, droneType: 'miner' | 'hauler' | 'fighter'): void {
    if (!this.room) return;
    this.room.send('purchaseDrone', { stationId, droneType });
  }

  public sendPurchaseShipDrone(droneType: 'miner' | 'hauler' | 'fighter'): void {
    if (!this.room) return;
    this.room.send('purchaseShipDrone', { droneType });
  }

  public sendDeployDrone(droneId: string): void {
    if (!this.room) return;
    this.room.send('deployDrone', { droneId });
  }

  public sendRecallDrone(droneId: string): void {
    if (!this.room) return;
    this.room.send('recallDrone', { droneId });
  }

  public sendListShipDrones(): void {
    if (!this.room) return;
    this.room.send('listShipDrones');
  }

  // DEV-ONLY: gated by VITE_ENABLE_DUMMY_SPAWNER on client + ENABLE_DUMMY_SPAWNER on server.
  private _lastDummySpawnMs = 0;
  public sendSpawnDummyEnemy(shipDefId: string, mode: 'passive' | 'hostile' | 'friendly', opts?: { weaponId?: string; utilityIds?: string[] }): void {
    if (!this.room) return;
    const now = Date.now();
    if (now - this._lastDummySpawnMs < 1500) return;
    this._lastDummySpawnMs = now;
    this.room.send('spawnDummyEnemy', { shipDefId, mode, ...opts });
  }

  public sendDespawnDummyEnemies(): void {
    if (!this.room) return;
    this.room.send('despawnDummyEnemies', {});
  }

  // DEV-ONLY: Change own ship and equipment in real-time (no DB writes)
  public sendDevChangeShip(shipClassId: string, equipment?: Record<number, string>): void {
    if (!this.room) return;
    this.room.send('devChangeShip', { shipClassId, equipment });
  }

  // DEV-ONLY: Teleport to arbitrary coordinates
  // Toggle deploy/recall all ship drones
  public sendToggleShipDrones(): void {
    if (!this.room) return;
    this.room.send('toggleShipDrones', {});
  }

  public sendDevTeleport(x: number, y: number): void {
    if (!this.room) return;
    this.room.send('devTeleport', { x, y });
  }

  // DEV-ONLY: Kill own ship (trigger normal death)
  public sendDevKill(): void {
    if (!this.room) return;
    this.room.send('devKill', {});
  }

  // Combat-overhaul: toggle natural shield up/down
  public sendShieldUp(): void {
    if (!this.room) return;
    this.room.send('shieldUp', {});
  }
  public sendShieldDown(): void {
    if (!this.room) return;
    this.room.send('shieldDown', {});
  }

  // Combat-overhaul: voluntary vent (Starsector-style)
  // Drains heat at 2× normal rate. Locks weapons + shield-raise until heat=0.
  public sendVent(): void {
    if (!this.room) return;
    this.room.send('vent', {});
  }

  // Combat-overhaul: beam fire start/stop (continuous heat for energy weapons)
  public sendBeamStart(): void {
    if (!this.room) return;
    this.room.send('beamStart', {});
  }
  public sendBeamEnd(): void {
    if (!this.room) return;
    this.room.send('beamEnd', {});
  }

  // Combat-overhaul: notify server of projectile fire (heat accrues on fire, not on hit)
  public sendWeaponFired(): void {
    if (!this.room) return;
    this.room.send('weaponFired', {});
  }

  // Combat-overhaul: switch which weapon slot is active (for cycle/scroll)
  public sendSetActiveWeapon(slotIndex: number): void {
    if (!this.room) return;
    this.room.send('setActiveWeapon', { slotIndex });
  }

  // DEV-ONLY: Get local player state snapshot for dev panel pre-fill
  public getDevPlayerState(): { x: number; y: number; shipClassId: string; slots: Array<{ slotType: string; size: string; equipped: string }> } | null {
    if (!this.room) return null;
    const state = this.room.state as any;
    const player = state.players?.get?.(this.room.sessionId);
    if (!player) return null;
    const slots: Array<{ slotType: string; size: string; equipped: string }> = [];
    player.slots?.forEach?.((slot: any) => {
      slots.push({ slotType: slot.slotType, size: slot.size, equipped: slot.equipped || '' });
    });
    return { x: player.x, y: player.y, shipClassId: player.shipClassId, slots };
  }

  public sendTransferFromDrop(dropId: string, itemType: 'credits' | 'resource' | 'equipment', itemId: string, quantity: number): void {
    if (!this.room) return;
    this.room.send('transferFromDrop', { dropId, itemType, itemId, quantity });
  }

  public sendTransferAllFromDrop(dropId: string): void {
    if (!this.room) return;
    this.room.send('transferAllFromDrop', { dropId });
  }

  public sendGetShipCargoSnapshot(): void {
    if (!this.room) return;
    this.room.send('getShipCargoSnapshot');
  }

  private extractSunData(sun: any, sunId: string): SunData {
    return {
      id: sunId,
      name: sun.name,
      x: sun.x,
      y: sun.y,
      radius: sun.radius,
      starType: sun.starType,
      damageRadius: sun.damageRadius,
      baseDamagePerSecond: sun.baseDamagePerSecond,
      sunSeed: sun.sunSeed ?? 0,
    };
  }

  private extractNebulaData(nebula: any, nebulaId: string): NebulaData {
    return {
      id: nebulaId,
      name: nebula.name,
      x: nebula.x,
      y: nebula.y,
      radius: nebula.radius,
      nebulaType: nebula.nebulaType,
      resourceType: nebula.resourceType,
      density: nebula.density,
      colorR: nebula.colorR,
      colorG: nebula.colorG,
      colorB: nebula.colorB,
    };
  }

  private extractGatewayData(gateway: any, gatewayId: string): GatewayData {
    return {
      id: gatewayId,
      targetConstellationId: gateway.targetConstellationId,
      targetConstellationName: gateway.targetConstellationName,
      targetConstellationX: gateway.targetConstellationX,
      targetConstellationY: gateway.targetConstellationY,
      travelCost: gateway.travelCost,
      x: gateway.x,
      y: gateway.y,
    };
  }

  // Input sequencing for server reconciliation (Phase 1 networking)
  private inputSequence: number = 0;

  // Send player input to server
  sendInput(x: number, y: number, rotation: number, velocityX: number, velocityY: number, thrusting: boolean, isBoosting: boolean = false): void {
    if (!this.room) return;

    // Increment sequence number for each input packet
    this.inputSequence++;

    this.room.send('input', {
      x,
      y,
      rotation,
      velocityX,
      velocityY,
      thrusting,
      isBoosting,
      timestamp: Date.now(),
      inputSequence: this.inputSequence,
    });
  }

  // Get current input sequence (for reconciliation)
  getInputSequence(): number {
    return this.inputSequence;
  }

  // Send mining action
  sendMine(asteroidId: string, damage: number): void {
    if (!this.room) return;

    this.room.send('mine', {
      asteroidId,
      damage,
    });
  }

  // Send fire event (for visual sync to other players)
  sendFire(x: number, y: number, angle: number, damage: number, speed: number, weaponType: 'laser' | 'kinetic', color: number): void {
    if (!this.room) return;

    this.room.send('fire', {
      x,
      y,
      angle,
      damage,
      speed,
      weaponType,
      color,
      timestamp: Date.now(), // For lag compensation
    });
  }

  // Send player attack (PvP - when player projectile hits another player)
  // timestamp: Client time when hit was detected (for lag compensation)
  sendPlayerAttack(
    targetPlayerId: string,
    damage: number,
    hitAngle: number,
    projectileX: number,
    projectileY: number,
    targetType: 'player' | 'drone' | 'settlement' = 'player'
  ): void {
    if (!this.room) return;

    this.room.send('playerAttack', {
      targetPlayerId,
      targetType,
      damage,
      hitAngle,
      projectileX,
      projectileY,
      timestamp: Date.now(), // For lag compensation
    });
  }

  // Send respawn request
  sendRespawn(x: number, y: number): void {
    if (!this.room) return;

    this.room.send('respawn', { x, y });
  }

  sendDock(stationId: string): void {
    if (!this.room) return;

    this.room.send('dock', { stationId });
  }

  sendDockingComplete(): void {
    if (!this.room) return;

    this.room.send('dockingComplete', {});
  }

  sendUndock(): void {
    if (!this.room) return;

    this.room.send('undock', {});
  }


  // ==================== GATEWAY METHODS ====================

  sendUseGateway(gatewayId: string): void {
    if (!this.room) return;

    this.room.send('use_gateway', { gatewayId });
  }

  // Tutorial v2 — four senders, all server-validated. Continue is the primary
  // path; missions are gated server-side and the client gets a tutorial_mission_
  // incomplete callback on rejection.
  //
  // 1. sendTutorialAdvance() — Continue. Server rejects if the current step has
  //    an active mission that's not yet complete (the panel shows the rejection
  //    hint from the tutorial_mission_incomplete event).
  // 2. sendTutorialAdvance({ force: true }) — Skip Step. Bypasses the mission
  //    gate; server tears down side-effects (drone despawn, baseline reset).
  // 3. sendTutorialPrev() — Prev. No-op at step 0. Server re-applies the
  //    previous step's side-effects (re-spawn mission target, re-snapshot baseline).
  // 4. sendTutorialSkip() — Skip the entire tutorial. Final for this character.
  sendTutorialAdvance(opts?: { force?: boolean }): void {
    this.room?.send('tutorial_advance', opts?.force ? { force: true } : {});
  }

  sendTutorialPrev(): void {
    this.room?.send('tutorial_prev', {});
  }

  sendTutorialSkip(): void {
    this.room?.send('tutorial_skip', {});
  }

  /** Ack the one-shot post-tutorial welcome modal so it never re-fires. */
  sendWelcomeToLiveSpaceAck(): void {
    this.room?.send('welcome_to_live_space_ack', {});
  }

  // ==================== PLANET MANAGEMENT PANEL ====================

  /** Ask the server to open the panel for `planetId`. Returns immediately;
   *  server responds with `planet_panel_snapshot` or `planet_panel_error`. */
  openPlanetPanel(planetId: string): void {
    if (!this.room) return;
    this.room.send('open_planet_panel', { planetId });
  }

  closePlanetPanel(): void {
    if (!this.room) return;
    this.room.send('close_planet_panel', {});
  }

  plantPlanetStructure(
    structureType: 'outpost' | 'mining_station' | 'relay' | 'scanner',
    lat: number,
    lon: number,
    /** Phase 4+ authoritative target hex. Optional for legacy callers. */
    hex_id?: number,
  ): void {
    if (!this.room) return;
    this.room.send('plant_structure', { structureType, lat, lon, hex_id });
  }

  dismantlePlanetStructure(structureId: string): void {
    if (!this.room) return;
    this.room.send('dismantle_structure', { structureId });
  }

  togglePlanetStructure(structureId: string, active: boolean): void {
    if (!this.room) return;
    this.room.send('toggle_structure', { structureId, active });
  }

  claimPlanetOutpostBuffer(outpostId: string): void {
    if (!this.room) return;
    this.room.send('claim_buffer', { outpostId });
  }

  requestPlanetScanDetails(lat: number, lon: number): void {
    if (!this.room) return;
    this.room.send('request_scan_details', { lat, lon });
  }

  // ==================== WARP TRAVEL METHODS ====================

  sendWarpEngage(targetX: number, targetY: number): void {
    if (!this.room) return;

    this.room.send('warpEngage', { targetX, targetY });
  }

  sendWarpCancel(): void {
    if (!this.room) return;

    this.room.send('warpCancel', {});
  }

  sendDeployBeacon(x: number, y: number): void {
    if (!this.room) return;

    this.room.send('deployBeacon', { x, y });
  }

  sendActivateDisruptor(): void {
    if (!this.room) return;
    this.room.send('activateDisruptor', {});
  }

  sendScanCargo(targetId: string): void {
    if (!this.room) return;
    this.room.send('scanCargo', { targetId });
  }

  // ==================== MINING METHODS ====================

  sendStartMining(asteroidId: string, kind?: string): void {
    if (!this.room) return;

    this.room.send('startMining', { asteroidId, kind: kind || 'asteroid' });
  }

  sendStopMining(asteroidId?: string): void {
    if (!this.room) return;

    this.room.send('stopMining', asteroidId ? { asteroidId } : {});
  }

  // sendMiningAnimationComplete removed — no dock animation in new mining flow

  // ==================== ORDER BOOK MARKET METHODS ====================
  // NOTE: NPC shop methods removed - all trading now uses order book market

  requestOrderBook(stationId: string, itemType: string, itemId: string): void {
    if (!this.room) return;
    this.room.send('getOrderBook', { stationId, itemType, itemId });
  }

  requestStationMarkets(stationId: string): void {
    if (!this.room) return;
    this.room.send('getStationMarkets', { stationId });
  }

  sendCreateLimitOrder(
    stationId: string,
    itemType: string,
    itemId: string,
    orderType: 'buy' | 'sell',
    price: number,
    quantity: number,
    destination?: 'cargo' | 'bank',
    source?: 'cargo' | 'bank'
  ): void {
    if (!this.room) return;
    this.room.send('createLimitOrder', { stationId, itemType, itemId, orderType, price, quantity, destination, source });
  }

  sendCreateMarketOrder(
    stationId: string,
    itemType: string,
    itemId: string,
    orderType: 'buy' | 'sell',
    quantity: number,
    destination?: 'cargo' | 'bank',
    source?: 'cargo' | 'bank'
  ): void {
    if (!this.room) return;
    this.room.send('createMarketOrder', { stationId, itemType, itemId, orderType, quantity, destination, source });
  }

  sendCancelOrder(orderId: string): void {
    if (!this.room) return;
    this.room.send('cancelOrder', { orderId });
  }

  requestMyOrders(stationId?: string): void {
    if (!this.room) return;
    this.room.send('getMyOrders', { stationId });
  }

  requestMarketHistory(stationId: string, itemType: string, itemId: string, limit?: number): void {
    if (!this.room) return;
    this.room.send('getMarketHistory', { stationId, itemType, itemId, limit });
  }

  requestMarketMakerPrices(stationId: string, itemId: string): void {
    if (!this.room) return;
    this.room.send('getMarketMakerPrices', { stationId, itemId });
  }

  requestPlayerInventory(stationId: string): void {
    if (!this.room) return;
    this.room.send('getPlayerInventory', { stationId });
  }

  // ==================== REPAIR SERVICES ====================

  sendRepairShip(stationId: string): void {
    if (!this.room) return;
    this.room.send('repairShip', { stationId });
  }

  requestRepairCost(): void {
    if (!this.room) return;
    this.room.send('getRepairCost', {});
  }

  // ==================== INVENTORY MANAGEMENT ====================

  requestInventoryData(stationId: string): void {
    if (!this.room) return;

    this.room.send('getInventoryData', { stationId });
  }

  sendEquipItem(stationId: string, equipmentId: string, slotIndex: number): void {
    if (!this.room) return;

    this.room.send('equipItem', { stationId, equipmentId, slotIndex });
  }

  sendUnequipItem(stationId: string, slotIndex: number): void {
    if (!this.room) return;

    this.room.send('unequipItem', { stationId, slotIndex });
  }

  sendDiscardItem(equipmentId: string, quantity: number = 1): void {
    if (!this.room) return;

    this.room.send('discardItem', { equipmentId, quantity });
  }

  // Bank storage methods
  requestPlayerBank(): void {
    if (!this.room) return;
    this.room.send('getPlayerBank', {});
  }

  sendShipToBank(equipmentId: string, quantity: number = 1): void {
    if (!this.room) return;
    this.room.send('shipToBank', { equipmentId, quantity });
  }

  sendBankToShip(itemType: 'equipment' | 'resource', itemId: string, quantity: number = 1): void {
    if (!this.room) return;
    this.room.send('bankToShip', { itemType, itemId, quantity });
  }

  // ==================== REFINING / CRAFTING ====================
  // The server resolves the shop from (docked station + recipe category) and the
  // recipe from the shared registry — the client only sends the recipe id + runs.
  sendRefine(stationId: string, recipeId: string, quantity: number = 1): void {
    if (!this.room) return;
    this.room.send('refine', { stationId, recipeId, quantity });
  }

  sendCraft(stationId: string, recipeId: string, quantity: number = 1): void {
    if (!this.room) return;
    this.room.send('craft', { stationId, recipeId, quantity });
  }

  // Shop ownership (Fase 2). Shops are addressed by category — no shop ids.
  requestShops(stationId: string): void {
    if (!this.room) return;
    this.room.send('getShops', { stationId });
  }
  sendClaimShop(stationId: string, category: string): void {
    if (!this.room) return;
    this.room.send('claimShop', { stationId, category });
  }
  sendReleaseShop(stationId: string, category: string): void {
    if (!this.room) return;
    this.room.send('releaseShop', { stationId, category });
  }
  sendSetShopFee(stationId: string, category: string, feePct: number): void {
    if (!this.room) return;
    this.room.send('setShopFee', { stationId, category, feePct });
  }
  requestAuctions(stationId: string): void {
    if (!this.room) return;
    this.room.send('getAuctions', { stationId });
  }
  sendPlaceBid(stationId: string, category: string, amount: number): void {
    if (!this.room) return;
    this.room.send('placeBid', { stationId, category, amount });
  }

  // ==================== STATION UI - PLAYER SHIPS ====================

  requestPlayerShips(stationId: string): void {
    if (!this.room) return;
    this.room.send('getPlayerShips', { stationId });
  }

  requestShipEquipment(shipId: string): void {
    if (!this.room) return;
    this.room.send('getShipEquipment', { shipId });
  }

  sendSwitchShip(shipId: string, stationId: string, transferEquipment?: boolean): void {
    if (!this.room) return;
    this.room.send('switchShip', { shipId, stationId, transferEquipment });
  }

  requestAllShopItems(stationId: string): void {
    if (!this.room) return;
    this.room.send('getAllShopItems', { stationId });
  }

  sendRenameShip(shipId: string, newName: string): void {
    if (!this.room) return;
    this.room.send('renameShip', { shipId, newName });
  }

  // ==================== CHAT ====================

  sendChatMessage(channel: ChatChannel, message: string): void {
    if (!this.room) return;
    this.room.send('chat', { channel, message });
  }

  // ==================== UNIVERSE MAP ====================

  requestUniverseMapData(): void {
    if (!this.room) return;
    this.room.send('getUniverseMap', {});
  }

  requestConstellationPreview(constellationX: number, constellationY: number): void {
    if (!this.room) return;
    this.room.send('getConstellationPreview', { constellationX, constellationY });
  }

  // ==================== BOUNTY SYSTEM ====================

  requestBountyList(): void {
    if (!this.room) return;
    this.room.send('requestBountyList', {});
  }

  // ==================== RADAR SCANNING ====================

  sendRadarScan(x: number, y: number, range: number): void {
    if (!this.room) return;
    this.room.send('radar_scan', { x, y, range });
  }

  sendInspectTarget(targetId: string): void {
    if (!this.room) return;
    this.room.send('inspectTarget', { targetId });
  }

  getLocalSessionId(): string | null {
    return this.room?.sessionId ?? null;
  }

  getConstellationCoords(): { x: number; y: number } {
    return { x: this.lastConstellationX, y: this.lastConstellationY };
  }

  getConstellationName(): string {
    return this.room?.state?.constellationName ?? `Constellation (${this.lastConstellationX}, ${this.lastConstellationY})`;
  }

  getConstellationTier(): number {
    return this.room?.state?.constellationTier ?? 1;
  }

  // Per-constellation physical size in world units. Falls back to the global default
  // when the room state hasn't arrived yet or the constellation predates the override
  // schema. Boundary clamp + boundary-warning + constellation map read this.
  getConstellationSize(): number {
    return (this.room?.state as any)?.constellationSize ?? WORLD_CONFIG.CONSTELLATION_SIZE;
  }

  getConstellationHalfSize(): number {
    return (this.room?.state as any)?.constellationHalfSize ?? WORLD_CONFIG.CONSTELLATION_SIZE / 2;
  }

  isTutorialConstellation(): boolean {
    return (this.room?.state as any)?.isTutorial === true;
  }

  // Last tutorial_state pushed by the server, or null if completed/not in tutorial.
  // Panel reads this on mount to recover from the panel-mounts-after-message race.
  getLatestTutorialState(): { step: number; title: string; body: string; totalSteps: number; mission: 'mine' | 'kill' | null } | null {
    return this.latestTutorialState;
  }

  getLatestTutorialMissionProgress(): { step: number; mission: 'mine' | 'kill'; progress: number; threshold: number; complete: boolean } | null {
    return this.latestTutorialMissionProgress;
  }

  getBeaconInRange(): boolean {
    return this.latestBeaconInRange;
  }

  setBeaconInRange(b: boolean): void {
    this.latestBeaconInRange = b;
  }

  getThreatLevel(): string {
    return (this.room?.state as any)?.threatLevel ?? 'green';
  }

  // --- RTT Measurement ---

  /** Get latest raw RTT in milliseconds */
  getRTT(): number {
    return this.rtt;
  }

  /** Get smoothed RTT in milliseconds */
  getSmoothedRTT(): number {
    return Math.round(this.rttSmoothed);
  }

  private startPingLoop(): void {
    this.stopPingLoop();
    // Send initial ping immediately
    if (this.room) {
      this.room.send('ping', { t: Date.now() });
    }
    this.pingInterval = setInterval(() => {
      if (this.room) {
        this.room.send('ping', { t: Date.now() });
      }
    }, this.PING_INTERVAL);
  }

  private stopPingLoop(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  getKillsInLastHour(): number {
    return (this.room?.state as any)?.killsInLastHour ?? 0;
  }

  isThreatCapped(): boolean {
    return (this.room?.state as any)?.threatCapped ?? false;
  }

  isConnected(): boolean {
    return this.room !== null;
  }

  disconnect(keepIntentionalFlag: boolean = false): Promise<void> {
    return new Promise((resolve) => {
      this.cancelReconnect();
      this.stopPingLoop();
      this.isIntentionalDisconnect = true;

      if (!this.room) {
        if (!keepIntentionalFlag) {
          this.isIntentionalDisconnect = false;
        }
        resolve();
        return;
      }

      // Set up one-time listener for leave completion
      const originalOnLeave = this.onDisconnected;
      this.onDisconnected = (code: number) => {
        this.onDisconnected = originalOnLeave;
        if (!keepIntentionalFlag) {
          this.isIntentionalDisconnect = false;
        }
        if (originalOnLeave) originalOnLeave(code);
        resolve();
      };

      this.room.leave();
      this.room = null;
    });
  }

  /**
   * Clear all event callbacks - useful when switching between different room types
   * (e.g., from ConstellationRoom to PlanetRoom) to prevent interference.
   * Nulls ALL public callback properties to prevent stale references to destroyed scenes.
   */
  clearCallbacks(): void {
    // Connection events
    this.onConnected = null;
    this.onDisconnected = null;
    this.onReconnecting = null;
    this.onReconnectFailed = null;
    this.onError = null;

    // Player events
    this.onLocalPlayerLoaded = null;
    this.onPlayerJoin = null;
    this.onPlayerLeave = null;
    this.onPlayerUpdate = null;
    this.onLocalPlayerStateChange = null;
    this.onLocalPlayerShipClassChange = null;
    this.onLocalPlayerCloakChange = null;
    this.onLocalPlayerCombatChange = null;
    this.onLocalPlayerReputationChange = null;
    this.onLocalPlayerEquipmentChange = null;
    this.onCreditsChange = null;

    // World events
    this.onAsteroidAdd = null;
    this.onAsteroidRemove = null;
    this.onAsteroidUpdate = null;
    this.onAsteroidDestroyed = null;
    // V1 harvestable callbacks
    this.onIceAsteroidAdd = null;
    this.onIceAsteroidRemove = null;
    this.onIceAsteroidUpdate = null;
    this.onGasPocketAdd = null;
    this.onGasPocketRemove = null;
    this.onGasPocketUpdate = null;
    this.onWreckageAdd = null;
    this.onWreckageRemove = null;
    this.onWreckageUpdate = null;
    this.onSystemAdd = null;
    this.onSystemRemove = null;
    this.onPlanetAdd = null;
    this.onPlanetChange = null;
    this.onPlanetRemove = null;
    this.onStationAdd = null;
    this.onPrivateStationAdd = null;
    this.onPrivateStationUpdate = null;
    this.onPrivateStationRemove = null;
    this.onSettlementAdd = null;
    this.onSettlementUpdate = null;
    this.onSettlementRemove = null;
    this.onSettlementHit = null;
    this.onSettlementDestroyed = null;
    this.onAsteroidZones = null;
    this.onResourceZones = null;
    this.outpostPlanetIds = new Set();

    // Combat events
    this.onPlayerProjectile = null;
    this.onDroneProjectile = null;
    this.onPlayerHit = null;
    this.onPlayerDestroyed = null;
    this.onShipLost = null;

    // Drop events
    this.onDropAdd = null;
    this.onDropRemove = null;
    this.onDropUpdate = null;
    this.onDropTransferResult = null;
    this.onShipCargoSnapshot = null;

    // Drone events
    this.onDroneAdd = null;
    this.onDroneRemove = null;
    this.onDroneUpdate = null;
    this.onDroneHit = null;
    this.onDroneDestroyed = null;
    this.onPurchaseDroneResult = null;
    this.onPurchaseShipDroneResult = null;
    this.onDeployDroneResult = null;
    this.onRecallDroneResult = null;
    this.onShipDronesList = null;

    // Sun/Nebula events
    this.onSunAdd = null;
    this.onSunRemove = null;
    this.onNebulaAdd = null;
    this.onNebulaRemove = null;

    // Gateway events
    this.onGatewayAdd = null;
    this.onGatewayRemove = null;
    this.onGatewayTravelStart = null;
    this.onGatewayTravelError = null;

    // Resource/Radiation events
    this.onResourceHarvested = null;
    this.onRadiationDamage = null;

    // Docking events
    this.onDockingStarted = null;
    this.onDocked = null;
    this.onUndocked = null;
    this.onDevTeleported = null;
    this.onToggleShipDronesResult = null;


    // Mining events
    this.onMiningStarted = null;
    this.onMiningStopped = null;
    this.onMiningTick = null;
    this.onAsteroidExploded = null;
    this.onMiningFailed = null;
    this.onPlayerMiningStarted = null;
    this.onPlayerMiningStopped = null;

    // Station services
    this.onStationShops = null;
    this.onRepairResult = null;
    this.onRepairCost = null;

    // Market events
    this.onOrderBookData = null;
    this.onStationMarketsData = null;
    this.onOrderResult = null;
    this.onMarketOrderResult = null;
    this.onCancelResult = null;
    this.onMyOrdersData = null;
    this.onPlayerInventoryData = null;
    this.onMarketHistoryData = null;
    this.onMarketMakerPricesData = null;
    this.onOrderBookUpdated = null;
    this.onRefineResult = null;
    this.onCraftResult = null;
    this.onShopsData = null;
    this.onShopActionResult = null;
    this.onAuctionsData = null;
    this.onBidResult = null;

    // Deprecated shop callbacks
    this.onShopInventory = null;
    this.onSellableItems = null;
    this.onAllSellableItems = null;
    this.onShopTransactionResult = null;

    // Inventory events
    this.onInventoryData = null;
    this.onEquipResult = null;
    this.onUnequipResult = null;
    this.onDiscardResult = null;

    // Bank events
    this.onPlayerBank = null;
    this.onBankTransferResult = null;

    // Ship management events
    this.onPlayerShips = null;
    this.onShipSwitchResult = null;
    this.onShipRenameResult = null;
    this.onAllShopItems = null;

    // Chat events
    this.onChatMessage = null;

    // Universe map events
    this.onUniverseMapData = null;
    this.onConstellationPreviewData = null;

    // Warp events
    this.onWarpStateChanged = null;
    this.onWarpInterrupted = null;
    this.onBeaconDeployed = null;
    this.onBeaconDeployError = null;

    // Cloak events
    this.onCloakBroken = null;

    // Protection events
    this.onTargetProtected = null;
    this.onProtectionLost = null;
    this.onEmergencyShipProvided = null;

    // Interdiction beacon events
    this.onInterdictionBeaconAdd = null;
    this.onInterdictionBeaconRemove = null;
    this.onInterdictionBeaconUpdate = null;

    // Bounty events
    this.onBountyListUpdate = null;
    this.onBountyClaimed = null;
    this.onBountyCleared = null;
  }

  getReconnectAttempts(): number {
    return this.reconnectAttempts;
  }

  isCurrentlyReconnecting(): boolean {
    return this.isReconnecting;
  }

  /** Current Colyseus session id, or empty string if no room. */
  getSessionId(): string {
    return this.room?.sessionId ?? '';
  }
}

// Singleton instance
export const networkManager = new NetworkManager();
